import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Checkbox from "@material-ui/core/Checkbox";
import styles from "./Table.module.scss";
import { Chip, Button, TextField, FormControlLabel, TableContainer, IconButton, Box } from "@material-ui/core";
import { getIcon } from "../../icon/icon";
import DropdownSelect from "../dropdown-select/DropdownSelect";
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { useTranslation } from "react-i18next";

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

function SimpleTableHead(props) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    isEditing
  } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {props.isSelectable &&
          <TableCell padding="checkbox">
            <Checkbox
              disabled={props.isEditing !== undefined && !props.isEditing}
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={numSelected === rowCount}
              onClick={() => onSelectAllClick(numSelected === rowCount)}
            />
          </TableCell>
        }
        {props.headers.map(row => (
          <TableCell
            key={row.id}
            align={row.align}
            sortDirection={orderBy === row.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === row.id}
              direction={order}
              onClick={createSortHandler(row.id)}
            >
              {row.label}
              {orderBy === row.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    height: "100%"
  },
  paper: {
    width: "100%",
    borderRadius: 0,
    boxShadow: "none",
    marginBottom: theme.spacing(2),
    backgroundColor: "transparent"
  },
  tableWrapper: {
    overflowX: "auto",
    height: "100%"
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 0,
    width: 1
  },
  tablePagination: {
    position: props => props.isSticky ? "sticky" : "fixed",
    bottom: "0",
    left: "0",
    right: "0"
  },
  tablePaginationRoot: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  }
}));

export const RowItemTypes = Object.freeze({
  TAG: "tag",
  TEXT_FIELD: "textField",
  CHECKBOX: "checkbox",
  DROPDOWN: "dropDown"
})

export default function SimpleTable(props) {
  const [t, i18n] = useTranslation()
  const classes = useStyles(props);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("userId");
  const [stateSelected, setStateSelected] = React.useState(props.selected || []);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  }

  function handleSelectAllClick(isChecked) {
    if (!isChecked) {

      let rows = props.rows.map(n => n[props.dataId]);

      const newSelecteds = props.checkDefault ? rows.filter(n => !props.checkDefault(n)) : rows;

      props.handleSelection(newSelecteds);
      setStateSelected(newSelecteds);
      return;
    }

    props.handleSelection([]);
    setStateSelected([]);
  }

  function handleCheck(event, name) {
    const selected = props.selected != null ? props.selected : stateSelected
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    props.handleSelection(newSelected);
    setStateSelected(newSelected);
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
    setPage(0);
  }

  function createRowItem(rowData, type, onChange, typeData) {
    if (type === RowItemTypes.TAG) {
      return <Chip size={"small"} label={String(rowData).toUpperCase()} />
    } else if (type === RowItemTypes.TEXT_FIELD) {
      return <TextField disabled={!props.isEditing} value={rowData} onChange={(event) => onChange(event.target.value)} margin="dense" variant="outlined" />
    } else if (type === RowItemTypes.CHECKBOX) {
      return <FormControlLabel
        control={
          <Checkbox
            disabled={!props.isEditing}
            checked={rowData}
            onChange={(event) => onChange(event.target.checked)}
            value="checkedB"
            color="primary"
          />
        }
        label={t('table.is-category')}
      />
    } else if (type === RowItemTypes.DROPDOWN) {
      return <DropdownSelect
        value={rowData}
        disabled={!props.isEditing}
        compulsory
        data={typeData != null ? typeData : []}
        handleChange={(data) => onChange(data)}
      />
    } else {
      return rowData;
    }
  }

  const isSelected = name => (props.selected != null ? props.selected : stateSelected).indexOf(name) !== -1;

  function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onChangePage } = props;

    const handleBackButtonClick = (event) => {
      onChangePage(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
      onChangePage(event, page + 1);
    };

    return (
      <Box className={classes.tablePaginationRoot}>
        <IconButton onClick={handleBackButtonClick} disabled={page == 0} aria-label="previous page">
          {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
      </Box>
    );
  }

  function tablePaginationRowLabels({ from, to, count, page }) {
    return (

      props.disableDisplayedRowsLabel ?
        t('table.page', { page: page }) :
        t('table.from-to', { from: from, to: to, count: (count !== -1 ? count : t('table.more-than', { value: to })) })
      // `${from}-${to} of ${count !== -1 ?
      //   count :
      //   `more than ${to}`}`
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.tableWrapper}>
        <TableContainer
          style={{ maxHeight: props.maxHeight }}
        >
          <Table
            stickyHeader
            className={styles.table}
            aria-labelledby="tableTitle"
            style={props.nonFixed ? null : { tableLayout: 'fixed' }}
          >
            <SimpleTableHead
              classes={classes}
              numSelected={(props.selected != null ? props.selected : stateSelected).length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={props.rows.length}
              headers={props.headers}
              isSelectable={props.isSelectable}
              isEditing={props.isEditing}
            />
            <TableBody>
              {stableSort(props.rows, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row[props.dataId]);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      className={styles.tableRow}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row[props.dataId]}
                      selected={isItemSelected}
                    >
                      {props.isSelectable &&
                        <TableCell padding="checkbox">
                          <Checkbox
                            disabled={(props.isEditing !== undefined && !props.isEditing) || (props.checkDefault ? props.checkDefault(row[props.dataId]) : false)}
                            onClick={event => handleCheck(event, row[props.dataId])}
                            checked={isItemSelected}
                            inputProps={{ "aria-labelledby": labelId }}
                          />
                        </TableCell>
                      }
                      {props.cells.map(cell => {
                        return (
                          <TableCell key={cell.id} align={cell.align}>
                            <span
                              onClick={event => {
                                if (cell.clickable) cell.handleClick(event, row);
                              }}
                              className={cell.clickable ? styles.rowName : undefined}
                            >
                              {createRowItem(row[cell.id] != null ? row[cell.id] : row[cell.secondaryId], cell.type, (data) => cell.onChange(data, row), cell.typeData)}
                            </span>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {props.hidePagination ?
        null :
        <TablePagination
          className={classes.tablePagination + " " + styles.tablePagination}
          rowsPerPageOptions={props.rowsPerPageOptions != null ? props.rowsPerPageOptions : [5, 10, 20, 50, 100, 200]}
          component="div"
          count={props.rows.length}
          rowsPerPage={props.rowsPerPage != null ? props.rowsPerPage : rowsPerPage}
          page={props.page != null ? props.page : page}
          backIconButtonProps={{
            "aria-label": "previous page"
          }}
          nextIconButtonProps={{
            "aria-label": "next page"
          }}
          labelDisplayedRows={tablePaginationRowLabels}
          onChangePage={props.onChangePage != null ? props.onChangePage : handleChangePage}
          onChangeRowsPerPage={props.onChangeRowsPerPage != null ? props.onChangeRowsPerPage : handleChangeRowsPerPage}
          ActionsComponent={props.customActions != null ? props.customActions : TablePaginationActions}
        />
      }
    </div>
  );
}
